import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as fromActions from './opcode.actions';
import * as fromAdapter from './opcode.adapter';
import { IAsyncData } from '../../shared/models/base.model';
import { IServiceOpcode, OnlineOpcodeSortBy, SortOrder, ISyncStatus, IOnlineCodeSortDetail, OpcodeType } from '@signal/asp-data-commons';

export interface IServiceOpcodes {
  [key: string]: IServiceOpcode[];
}
export interface AllOpCodeState extends IAsyncData, EntityState<IServiceOpcode> {
  opCodeCount: number;
  syncStatus: ISyncStatus;
  isOpcodeExists: boolean;
  isAspCodeCreated:IServiceOpcode;
}

export interface OnlineOpCodeState extends IAsyncData, EntityState<IServiceOpcode> {
  opCodeCount: number;
}

export interface MileageState extends IAsyncData, EntityState<number> {
  mileages: number[];
}

export interface OpCodeState extends IAsyncData {
  selectedOpCode: string | null;
  onlineSortOrder: IOnlineCodeSortDetail;
  allOpCodes: AllOpCodeState;
  onlineOpCodes: OnlineOpCodeState;
  mileages: MileageState;
}

export const allOpCodesAdapter: EntityAdapter<IServiceOpcode> = createEntityAdapter<IServiceOpcode>(
  { selectId: (service: IServiceOpcode) => service.opcode }
);

export function sortByName(a: IServiceOpcode, b: IServiceOpcode): number {
  const aDisplayName = a.displayName || a.dmsDescription;
  const bDisplayName = b.displayName || b.dmsDescription;
  return aDisplayName.localeCompare(bDisplayName);
}

export const onlineOpCodesAdapter: EntityAdapter<IServiceOpcode> = createEntityAdapter<IServiceOpcode>(
  {
    selectId: (service: IServiceOpcode) => service.opcode,
    sortComparer: sortByName
  }
);

export const mileagesAdapter: EntityAdapter<number> = createEntityAdapter<number>(
  { selectId: (mileage: number) => mileage }
);

export const allOpCodesInitialState: AllOpCodeState = allOpCodesAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: '',
  selectedOpCode: null,
  opCodeCount: 0,
  syncStatus: { status: '' },
  isOpcodeExists: false,
  isAspCodeCreated : null
});

export const onlineOpCodesInitialState: OnlineOpCodeState = onlineOpCodesAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: '',
  opCodeCount: 0
});

export const mileagesInitialState: MileageState = mileagesAdapter.getInitialState({
  loading: false,
  loaded: false,
  error: '',
  mileages: []
})

export const initialState: OpCodeState = {
  loading: false,
  loaded: false,
  error: '',
  selectedOpCode: null,
  onlineSortOrder: {
    onlineCustomSortOrder: {
      maintenance: [],
      complaint: [],
      dms: []
    },
    onlineOpcodeSortDetail: {
      by: {
        maintenance: OnlineOpcodeSortBy.ALPHA,
        complaint: OnlineOpcodeSortBy.ALPHA,
        dms: OnlineOpcodeSortBy.ALPHA
      },
      order: SortOrder.ASC
    }
  }
  ,
  allOpCodes: allOpCodesInitialState,
  onlineOpCodes: onlineOpCodesInitialState,
  mileages: mileagesInitialState
};

export function opCodeReducer(state: OpCodeState = initialState,
  action: fromActions.OpCodeActions): OpCodeState {

  switch (action.type) {

    case fromActions.OpCodeActionTypes.LOAD_ALL_OPCODES: {
      return {
        ...state,
        allOpCodes: {
          ...allOpCodesAdapter.removeAll(state.allOpCodes),
          loaded: false,
          loading: true,
          opCodeCount: 0
        },
        selectedOpCode: null
      };

    }
    case fromActions.OpCodeActionTypes.LOAD_ALL_OPCODES_SUCCESS: {
      return {
        ...state,
        allOpCodes: {
          ...allOpCodesAdapter.setAll(action instanceof fromActions.LoadAllOpCodesSuccess ? action.payload.opCodeDetails.result : [], state.allOpCodes),
          opCodeCount: action instanceof fromActions.LoadAllOpCodesSuccess ? action.payload.opCodeDetails.result.length : 0,
          loaded: true,
          loading: false,
          error: ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_ALL_OPCODES_FAILURE: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          loaded: false,
          loading: false,
          error: action instanceof fromActions.LoadAllOpCodesFailure ? action.payload.error : ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_SYNC_STATUS: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          syncStatus: { status: '' }
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_SYNC_STATUS_SUCCESS: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          syncStatus: action instanceof fromActions.LoadSyncStatusSuccess ? action.payload.status : null
        }
      };
    }
    case fromActions.OpCodeActionTypes.UPDATE_FROM_DMS_SUCCESS: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          syncStatus: action instanceof fromActions.UpdateFromDmsSuccess ? action.payload.status : null
        }
      };
    }
    case fromActions.OpCodeActionTypes.UPDATE_FROM_DMS_FAILURE: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          error: action instanceof fromActions.UpdateFromDmsFailure ? action.payload.error : ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_SYNC_STATUS_FAILURE: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          error: action instanceof fromActions.LoadSyncStatusFailure ? action.payload.error : ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_ONLINE_OPCODES: {
      return {
        ...state,
        onlineOpCodes: {
          ...onlineOpCodesAdapter.removeAll(state.onlineOpCodes),
          opCodeCount: 0,
          loading: true,
          loaded: false
        },
        selectedOpCode: null
      };

    }
    case fromActions.OpCodeActionTypes.LOAD_ONLINE_OPCODES_SUCCESS: {
      return {
        ...state,
        onlineOpCodes: {
          ...onlineOpCodesAdapter.setAll(action instanceof fromActions.LoadOnlineOpCodesSuccess ? action.payload.opCodeDetails.result : [], state.onlineOpCodes),
          opCodeCount: action instanceof fromActions.LoadOnlineOpCodesSuccess ? action.payload.opCodeDetails.result.length : 0,
          loaded: true,
          loading: false,
          error: ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_ONLINE_OPCODES_FAILURE: {
      return {
        ...state,
        onlineOpCodes: {
          ...state.onlineOpCodes,
          loaded: false,
          loading: false,
          error: action instanceof fromActions.LoadOnlineOpCodesFailure ? action.payload.error : ''
        }
      };
    }

    case fromActions.OpCodeActionTypes.LOAD_OPCODE: {
      return {
        ...state,
        selectedOpCode: action instanceof fromActions.LoadOpCode ? action.payload.opcode : null
      };
    }
   
    case fromActions.OpCodeActionTypes.UPDATE_OPCODE: {
      const payload = action instanceof fromActions.UpdateOpCode ? action.payload : null;
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes
        },
        onlineOpCodes: {
          ...state.onlineOpCodes
        }
      };
    }
    case fromActions.OpCodeActionTypes.UPDATE_OPCODE_SUCCESS: {
      const payload = action instanceof fromActions.UpdateOpCodeSuccess ? action.payload : null;
      return {
        ...state,
        allOpCodes: {
          ...allOpCodesAdapter.map(opcode => fromAdapter.updateRelatedAddOnOpcodes(opcode, payload, 'update'), state.allOpCodes),
          ...(payload.opcode.isMiscellaneous ?
            (allOpCodesAdapter.updateMany(Object.keys(state.allOpCodes.entities)
              .filter((key) => state.allOpCodes.entities[key].isMiscellaneous)
              .map((key) =>
              ({
                id: key, changes: { ...state.allOpCodes.entities[key], isMiscellaneous: false }
              })
              ), state.allOpCodes)) : {}),
          ...allOpCodesAdapter.updateOne({ id: payload.opcode.opcode, changes: payload.opcode }, state.allOpCodes)
        },
        onlineOpCodes: {
          ...onlineOpCodesAdapter.map(opcode => fromAdapter.updateRelatedAddOnOpcodes(opcode, payload, 'update'), state.onlineOpCodes),
          ...(payload.opcode.isMiscellaneous ?
            (onlineOpCodesAdapter.removeMany(Object.keys(state.onlineOpCodes.entities)
              .filter((key) => state.onlineOpCodes.entities[key].isMiscellaneous && key !== payload.opcode.opcode)
              .map((key) => key), state.onlineOpCodes)) : {}),
          ...(payload.opcode.isVisible ? onlineOpCodesAdapter.setOne(payload.opcode, state.onlineOpCodes) :
            onlineOpCodesAdapter.removeOne(payload.opcode.opcode, state.onlineOpCodes)),
            opCodeCount : payload.opcode.isVisible ? state.onlineOpCodes.opCodeCount + 1 : state.onlineOpCodes.opCodeCount - 1
        }
      };
    }
    case fromActions.OpCodeActionTypes.UPDATE_OPCODE_FAILURE: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          loaded: false,
          loading: false,
          error: action instanceof fromActions.UpdateOpCodeFailure ? action.payload.error : ''
        },
        onlineOpCodes: {
          ...state.onlineOpCodes,
          loaded: false,
          loading: false,
          error: action instanceof fromActions.UpdateOpCodeFailure ? action.payload.error : ''
        }
      };
    }

    case fromActions.OpCodeActionTypes.LOAD_MILEAGES: {
      return {
        ...state,
        mileages: {
          ...mileagesAdapter.removeAll(state.mileages),
          loading: true,
          loaded: false,
          error: ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_MILEAGES_SUCCESS: {
      return {
        ...state,
        mileages: {
          ...state.mileages,
          ...mileagesAdapter.setAll(action instanceof fromActions.LoadMileagesSuccess ? action.payload.mileages || [] : [], state.mileages),
          loading: true,
          loaded: false,
          error: ''
        }
      };
    }
    case fromActions.OpCodeActionTypes.LOAD_MILEAGES_FAILURE: {
      return {
        ...state,
        mileages: {
          ...state.mileages,
          loaded: false,
          loading: false,
          error: action instanceof fromActions.LoadMileagesFailure ? action.payload.error : ''
        }
      }
    }

    case fromActions.OpCodeActionTypes.LOAD_ONLINE_SORT_ORDER: {
      return {
        ...state,
        loaded: false,
        loading: true,
        error: ''
      };
    }

    case fromActions.OpCodeActionTypes.LOAD_ONLINE_SORT_ORDER_SUCCESS: {
      return {
        ...state,
        onlineSortOrder: action instanceof fromActions.LoadOnlineSortOrderSuccess ? action.payload.onlineSortOrder : state.onlineSortOrder,
        loaded: true,
        loading: false,
        error: ''
      };
    }

    case fromActions.OpCodeActionTypes.LOAD_ONLINE_SORT_ORDER_FAILURE: {
      return {
        ...state,
        loaded: false,
        loading: false,
        error: action instanceof fromActions.LoadOnlineSortOrderFailure ? action.payload.error : ''
      }
    }

    case fromActions.OpCodeActionTypes.UPDATE_ONLINE_SORT_ORDER: {
      return {
        ...state,
        loaded: false,
        loading: true,
        error: ''
      };
    }

    case fromActions.OpCodeActionTypes.UPDATE_ONLINE_SORT_ORDER_SUCCESS: {
      return {
        ...state,
        onlineSortOrder: action instanceof fromActions.UpdateOnlineSortOrderSuccess ? action.payload.onlineSortOrder : state.onlineSortOrder,
        loaded: true,
        loading: false,
        error: ''
      };
    }

    case fromActions.OpCodeActionTypes.UPDATE_ONLINE_SORT_ORDER_FAILURE: {
      return {
        ...state,
        loaded: false,
        loading: false,
        error: action instanceof fromActions.LoadOnlineSortOrderFailure ? action.payload.error : ''
      }
    }

    case fromActions.OpCodeActionTypes.CREATE_OPCODE: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes
        },
        onlineOpCodes: {
          ...state.onlineOpCodes
        }
      };
    }
    case fromActions.OpCodeActionTypes.CREATE_OPCODE_SUCCESS: {
      const payload = action instanceof fromActions.CreateOpCodeSuccess ? action.payload : null;
      const opcode = {
        ...payload.opcode,
        'PK': `DLR#${payload.opcode.dealerCode}`,
        'SK': payload.opcode.opcodeType===OpcodeType.MAINTENANCE ?  `MAINTENANCECODE#${payload.opcode.opcode}`: `COMPLAINTCODE#${payload.opcode.opcode}`
      }
      return {
        ...state,
        allOpCodes: {
          ...(allOpCodesAdapter.setAll([opcode ,...allOpCodesAdapter.getSelectors().selectAll(state.allOpCodes)],state.allOpCodes)),
          opCodeCount : state.allOpCodes.opCodeCount+1
        },
        onlineOpCodes: {
          ...(opcode.isVisible === true ? onlineOpCodesAdapter.setOne(opcode, state.onlineOpCodes) :
          state.onlineOpCodes ),
          opCodeCount: (opcode.isVisible === true ? state.onlineOpCodes.opCodeCount+1 : state.onlineOpCodes.opCodeCount)
        }
      };
    }
    case fromActions.OpCodeActionTypes.CREATE_OPCODE_FAILURE: {
      return {
        ...state,
        allOpCodes: {
          ...state.allOpCodes,
          error: action instanceof fromActions.CreateOpCodeFailure ? action.payload.error : ''
        }
      };
    }

    default:
      return state;
  }
}
